'use client'
import { useEffect, useRef, useState } from 'react'
import useIsSsr from 'hooks/useIsSsr'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import { AchieveTheme } from '@achieve/ascend'

function SlideOnVisible({
  duration = 1600,
  timeout = 700,
  scrollOnMobile = true,
  direction = 'up',
  children,
  easing,
}) {
  const isSsr = useIsSsr()
  const [isVisible, setIsVisible] = useState(false)
  const [animationComplete, setAnimationComplete] = useState(false)
  const elementRef = useRef(null)
  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)

  useEffect(() => {
    if (isSsr) {
      // no window to measure during SSR
      return
    }

    const currentElement = elementRef.current
    if (!currentElement) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true)
            setTimeout(() => {
              setAnimationComplete(true)
            }, Math.max(duration, (AchieveTheme?.transitions?.duration?.short || 0) * 4))
            observer.unobserve(currentElement)
          }, timeout)
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    )

    observer.observe(currentElement)

    return () => {
      observer.unobserve(currentElement)
    }
  }, [isSsr, timeout, duration])

  if (!scrollOnMobile && isMobile) {
    return <div ref={elementRef}>{children}</div>
  }

  const getTransform = (isElementVisible) => {
    if (isElementVisible) {
      return 'translate(0, 0)'
    }
    switch (direction) {
      case 'up':
        return 'translateY(100%)'
      case 'down':
        return 'translateY(-100%)'
      case 'left':
        return 'translateX(100%)'
      case 'right':
        return 'translateX(-100%)'
      default:
        return 'none'
    }
  }

  const animationDuration = (AchieveTheme?.transitions?.duration?.short || 0) * 4
  const animationEasing = AchieveTheme?.transitions?.easing?.easeInOut

  const outerStyle = {
    opacity: isVisible ? 1 : 0,
    transition: `opacity ${animationDuration}ms ${animationEasing}`,
  }

  const innerStyle = {
    // Remove transform after animation completes to eliminate the containing block
    // Without this, the badge is positioned incorrectly
    transform: animationComplete ? 'none' : getTransform(isVisible),
    transition: animationComplete ? 'none' : `transform ${duration}ms ${easing}`,
  }

  return (
    <div ref={elementRef} style={outerStyle}>
      <div style={innerStyle}>{children}</div>
    </div>
  )
}
export default SlideOnVisible
